import { api } from './axios';

/**
 * Валидация ID
 * @param {string|number} id - Идентификатор
 * @param {string} name - Название поля для ошибки
 */
const validateId = (id, name = 'ID') => {
    if (!id && id !== 0) throw new Error(`${name} is required`);
};

/**
 * API для работы с форумом
 */
export const forumApi = {
    /**
     * Получает список постов
     * @param {Object} params - Параметры пагинации и фильтрации
     * @param {number} [params.page=0]
     * @param {number} [params.size=10]
     * @param {string} [params.sortBy='createdAt']
     * @param {string} [params.direction='DESC']
     * @param {string} [params.status]
     * @param {string} [params.categoryId]
     * @param {string} [params.search]
     * @param {AbortSignal} [signal] - Сигнал отмены
     * @returns {Promise<Object>} Пагинированный ответ { content, totalElements, ... }
     */
    listPosts: async (params = {}, signal = null) => {
        try {
            const { data } = await api.get('/forum/posts', { params, signal });
            return data;
        } catch (error) {
            console.error('[forumApi.listPosts] Failed to fetch posts:', error.message);
            throw error;
        }
    },

    /**
     * Получает пост по ID
     * @param {string|number} id - ID поста
     * @returns {Promise<Object>}
     */
    getPost: async (id) => {
        validateId(id, 'Post ID');
        try {
            const { data } = await api.get(`/forum/posts/${id}`);
            return data;
        } catch (error) {
            console.error(`[forumApi.getPost] Failed to fetch post ${id}:`, error.message);
            throw error;
        }
    },

    /**
     * Создаёт новый пост
     * @param {Object} payload - Данные поста
     * @param {string} payload.title - Заголовок поста
     * @param {string} payload.content - Содержание поста
     * @param {string} payload.authorId - ID автора
     * @param {string} [payload.categoryId] - ID категории
     * @param {string[]} [payload.tags] - Теги
     * @param {string[]} [payload.attachmentIds] - ID вложений
     * @returns {Promise<Object>}
     */
    createPost: async (payload) => {
        if (!payload) throw new Error('Post payload is required');
        try {
            const { data } = await api.post('/forum/posts', payload);
            return data;
        } catch (error) {
            console.error('[forumApi.createPost] Failed to create post:', error.message);
            throw error;
        }
    },

    /**
     * Обновляет пост
     * @param {string|number} id - ID поста
     * @param {Object} payload - Поля для обновления
     * @returns {Promise<Object>}
     */
    updatePost: async (id, payload) => {
        validateId(id, 'Post ID');
        if (!payload) throw new Error('Update payload is required');
        try {
            const { data } = await api.put(`/forum/posts/${id}`, payload);
            return data;
        } catch (error) {
            console.error(`[forumApi.updatePost] Failed to update post ${id}:`, error.message);
            throw error;
        }
    },

    /**
     * Удаляет пост
     * @param {string|number} id - ID поста
     * @returns {Promise<Object>} Ответ от сервера
     */
    deletePost: async (id) => {
        validateId(id, 'Post ID');
        try {
            const { data } = await api.delete(`/forum/posts/${id}`);
            return data;
        } catch (error) {
            console.error(`[forumApi.deletePost] Failed to delete post ${id}:`, error.message);
            throw error;
        }
    },

    /**
     * Получает список категорий
     * @returns {Promise<Array>}
     */
    listCategories: async () => {
        try {
            const { data } = await api.get('/forum/categories');
            return data;
        } catch (error) {
            console.error('[forumApi.listCategories] Failed to fetch categories:', error.message);
            throw error;
        }
    },

    /**
     * Создаёт категорию
     * @param {Object} payload - Данные категории
     * @returns {Promise<Object>}
     */
    createCategory: async (payload) => {
        if (!payload) throw new Error('Category payload is required');
        try {
            const { data } = await api.post('/forum/categories', payload);
            return data;
        } catch (error) {
            console.error('[forumApi.createCategory] Failed to create category:', error.message);
            throw error;
        }
    },

    /**
     * Обновляет категорию
     * @param {string|number} id - ID категории
     * @param {Object} payload - Поля для обновления
     * @returns {Promise<Object>}
     */
    updateCategory: async (id, payload) => {
        validateId(id, 'Category ID');
        if (!payload) throw new Error('Update payload is required');
        try {
            const { data } = await api.put(`/forum/categories/${id}`, payload);
            return data;
        } catch (error) {
            console.error(`[forumApi.updateCategory] Failed to update category ${id}:`, error.message);
            throw error;
        }
    },

    /**
     * Удаляет категорию
     * @param {string|number} id - ID категории
     * @returns {Promise<Object>} Ответ от сервера
     */
    deleteCategory: async (id) => {
        validateId(id, 'Category ID');
        try {
            const { data } = await api.delete(`/forum/categories/${id}`);
            return data;
        } catch (error) {
            console.error(`[forumApi.deleteCategory] Failed to delete category ${id}:`, error.message);
            throw error;
        }
    },

    // 🔴 Этап 4: Forum Enhancements
    /**
     * Получает комментарии к посту
     * @param {string|number} postId - ID поста
     * @param {Object} params - Параметры пагинации (page, size, thread)
     * @returns {Promise<Object>}
     */
    getPostComments: async (postId, params = {}) => {
        validateId(postId, 'Post ID');
        try {
            const { data } = await api.get(`/forum/posts/${postId}/comments`, { params });
            return data;
        } catch (error) {
            console.error(`[forumApi.getPostComments] Failed to fetch comments for post ${postId}:`, error.message);
            throw error;
        }
    },

    /**
     * Добавляет комментарий к посту
     * @param {string|number} postId - ID поста
     * @param {Object} payload - Данные комментария
     * @param {string} payload.content - Содержание комментария
     * @param {string} payload.authorId - ID автора
     * @param {string} [payload.parentCommentId] - ID родительского комментария
     * @param {string[]} [payload.attachmentIds] - ID вложений
     * @returns {Promise<Object>}
     */
    addCommentToPost: async (postId, payload) => {
        validateId(postId, 'Post ID');
        if (!payload) throw new Error('Comment payload is required');
        try {
            const { data } = await api.post(`/forum/posts/${postId}/comments`, payload);
            return data;
        } catch (error) {
            console.error(`[forumApi.addCommentToPost] Failed to add comment to post ${postId}:`, error.message);
            throw error;
        }
    },

    /**
     * Создаёт комментарий (альтернативный endpoint)
     * @param {Object} payload - Данные комментария
     * @param {string} payload.content - Содержание комментария
     * @param {string} payload.postId - ID поста
     * @param {string} [payload.parentCommentId] - ID родительского комментария
     * @param {string[]} [payload.attachmentIds] - ID вложений
     * @returns {Promise<Object>}
     */
    createComment: async (payload) => {
        if (!payload) throw new Error('Comment payload is required');
        if (!payload.postId) throw new Error('postId is required');
        try {
            const { data } = await api.post('/forum/comments', payload);
            return data;
        } catch (error) {
            console.error('[forumApi.createComment] Failed to create comment:', error.message);
            throw error;
        }
    },

    /**
     * Обновляет комментарий
     * @param {string|number} commentId - ID комментария
     * @param {Object} payload - Данные для обновления
     * @param {string} [payload.content] - Новое содержание
     * @param {string[]} [payload.attachmentIds] - ID вложений
     * @returns {Promise<Object>}
     */
    updateComment: async (commentId, payload) => {
        validateId(commentId, 'Comment ID');
        if (!payload) throw new Error('Update payload is required');
        try {
            const { data } = await api.patch(`/forum/comments/${commentId}`, payload);
            return data;
        } catch (error) {
            console.error(`[forumApi.updateComment] Failed to update comment ${commentId}:`, error.message);
            throw error;
        }
    },

    /**
     * Лайк поста
     * @param {string|number} postId - ID поста
     * @returns {Promise<Object>}
     */
    likePost: async (postId) => {
        validateId(postId, 'Post ID');
        try {
            const { data } = await api.post(`/forum/posts/${postId}/like`);
            return data;
        } catch (error) {
            console.error(`[forumApi.likePost] Failed to like post ${postId}:`, error.message);
            throw error;
        }
    },

    /**
     * Удалить лайк поста
     * @param {string|number} postId - ID поста
     * @returns {Promise<Object>}
     */
    unlikePost: async (postId) => {
        validateId(postId, 'Post ID');
        try {
            const { data } = await api.delete(`/forum/posts/${postId}/like`);
            return data;
        } catch (error) {
            console.error(`[forumApi.unlikePost] Failed to unlike post ${postId}:`, error.message);
            throw error;
        }
    },

    /**
     * Изменить статус поста (MODERATOR+)
     * @param {string|number} postId - ID поста
     * @param {string} status - PUBLISHED | HIDDEN | DELETED
     * @returns {Promise<Object>}
     */
    updatePostStatus: async (postId, status) => {
        validateId(postId, 'Post ID');
        if (!status) throw new Error('Status is required');
        try {
            const { data } = await api.patch(`/forum/posts/${postId}/status`, { status });
            return data;
        } catch (error) {
            console.error(`[forumApi.updatePostStatus] Failed to update post ${postId} status:`, error.message);
            throw error;
        }
    },

    /**
     * Лайк комментария
     * @param {string|number} commentId - ID комментария
     * @returns {Promise<Object>}
     */
    likeComment: async (commentId) => {
        validateId(commentId, 'Comment ID');
        try {
            const { data } = await api.post(`/forum/comments/${commentId}/like`);
            return data;
        } catch (error) {
            console.error(`[forumApi.likeComment] Failed to like comment ${commentId}:`, error.message);
            throw error;
        }
    },

    /**
     * Удалить лайк комментария
     * @param {string|number} commentId - ID комментария
     * @returns {Promise<Object>}
     */
    unlikeComment: async (commentId) => {
        validateId(commentId, 'Comment ID');
        try {
            const { data } = await api.delete(`/forum/comments/${commentId}/like`);
            return data;
        } catch (error) {
            console.error(`[forumApi.unlikeComment] Failed to unlike comment ${commentId}:`, error.message);
            throw error;
        }
    },

    /**
     * Изменить статус комментария (MODERATOR+)
     * @param {string|number} commentId - ID комментария
     * @param {string} status - PUBLISHED | HIDDEN | DELETED
     * @returns {Promise<Object>}
     */
    updateCommentStatus: async (commentId, status) => {
        validateId(commentId, 'Comment ID');
        if (!status) throw new Error('Status is required');
        try {
            const { data } = await api.patch(`/forum/comments/${commentId}/status`, { status });
            return data;
        } catch (error) {
            console.error(`[forumApi.updateCommentStatus] Failed to update comment ${commentId} status:`, error.message);
            throw error;
        }
    },

    /**
     * Удаляет комментарий (автор или MODERATOR+)
     * @param {string|number} commentId - ID комментария
     * @returns {Promise<Object>} Ответ от сервера
     */
    deleteComment: async (commentId) => {
        validateId(commentId, 'Comment ID');
        try {
            const { data } = await api.delete(`/forum/comments/${commentId}`);
            return data;
        } catch (error) {
            console.error(`[forumApi.deleteComment] Failed to delete comment ${commentId}:`, error.message);
            throw error;
        }
    },

    /**
     * Полнотекстовый поиск
     * @param {Object} params - Параметры поиска (q - required, tag, page, size)
     * @returns {Promise<Object>}
     */
    search: async (params) => {
        if (!params?.q) throw new Error('Search query (q) is required');
        try {
            const { data } = await api.get('/forum/search', { params });
            return data;
        } catch (error) {
            console.error('[forumApi.search] Failed to search forum:', error.message);
            throw error;
        }
    },

    /**
     * Получает список всех тегов
     * @returns {Promise<Array>}
     */
    listTags: async () => {
        try {
            const { data } = await api.get('/forum/tags');
            return data;
        } catch (error) {
            console.error('[forumApi.listTags] Failed to fetch tags:', error.message);
            throw error;
        }
    },

    /**
     * Получает посты по тегу
     * @param {string} slug - Slug тега
     * @param {Object} params - Параметры пагинации
     * @returns {Promise<Object>}
     */
    getPostsByTag: async (slug, params = {}) => {
        if (!slug) throw new Error('Tag slug is required');
        try {
            const { data } = await api.get(`/forum/tags/${slug}/posts`, { params });
            return data;
        } catch (error) {
            console.error(`[forumApi.getPostsByTag] Failed to fetch posts for tag ${slug}:`, error.message);
            throw error;
        }
    },

    /**
     * Получает все комментарии (ADMIN)
     * Endpoint: GET /api/forum/comments
     * @returns {Promise<Array>}
     */
    getAllComments: async () => {
        try {
            const { data } = await api.get('/forum/comments');
            return data;
        } catch (error) {
            console.error('[forumApi.getAllComments] Failed to fetch all comments:', error.message);
            throw error;
        }
    }
};